//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Menu, TextField } from '@mui/material';
import FilterChip from './FilterChip';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { createSelector } from '@reduxjs/toolkit';

export interface FilterProps {
  label: string;
  options?: Record<string, string>;
  type: string;
  component?: React.FC;
  role?: ReturnType<typeof createSelector>;
}

export default function FilterText({ label, type, component, role }: FilterProps) {
  const hasPermissions = role ? useAppSelector(role) : true;
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDataTableFilters);
  const selectedOptions = useMemo(() => filters[type] || [], [filters]);
  const CustomComponent = component;

  const handleClick = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const hasSelections = () => selectedOptions.length !== 0;

  const [value, setValue] = useState(selectedOptions ? selectedOptions[0] : '');
  const handleSetValue = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setValue(e.target.value);

  const getApplied = () => {
    if (!selectedOptions.length) return 'All';
    return selectedOptions[0];
  };

  const handleClear = (e: any) => {
    e.stopPropagation();
    dispatch(applyFilter({ type, selections: '' }));
  };
  const onWrapperKeyDown = (e: any) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      handleClose();
    }
    if (e.key === 'Enter') {
      dispatch(applyFilter({ type, selections: value.length ? [value] : [] }));
      setAnchorEl(null);
    }
  };

  useEffect(() => {
    setValue(selectedOptions ? selectedOptions[0] : '');
  }, [selectedOptions]);

  return hasPermissions ? (
    <div style={{ marginRight: 10 }}>
      <FilterChip
        label={
          <>
            {label}: <strong style={{ marginLeft: 5 }}>{getApplied()}</strong>
          </>
        }
        isSelected={hasSelections()}
        onClick={handleClick as any}
        onClear={handleClear as any}
      />

      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        variant="menu"
      >
        <div onKeyDown={onWrapperKeyDown}>
          {CustomComponent ? (
            <CustomComponent />
          ) : (
            <SearchBox
              role="combobox"
              label={label}
              aria-labelledby="filter"
              aria-haspopup="listbox"
              aria-expanded="true"
              aria-controls="select-dropdown"
              id="program_id"
              autoFocus
              name="filter search"
              autoComplete="off"
              variant="outlined"
              color="primary"
              value={value}
              onChange={handleSetValue}
              InputProps={{
                inputProps: {
                  'data-testid': 'filter-search-input'
                },
                tabIndex: 0
              }}
            />
          )}
        </div>
      </Menu>
    </div>
  ) : (
    <></>
  );
}

const SearchBox = styled(TextField)`
  && {
    margin: ${(props) => props.theme.spacing(2)};
    min-width: 150px;

    & .MuiInputBase-root {
      padding-right: 0;
      font-size: 1rem;
    }

    & .MuiInputLabel-root {
      font-size: 1rem;
      line-height: 2rem;
    }
  }
`;
